import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlus, IconTruckDelivery } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { datumZaApi } from '../oblika/oblika';
import { obrezano, type VoziloStranke } from './delavnica';
import { IzbiraVozila } from './IzbiraVozila';
import { PoljaVozila } from './PoljaVozila';
import { StrankaNaloga } from './StrankaNaloga';
import { ustvariStranko, vpisanaNova, type NovaStranka } from './stranka';
import { imeDne, TRAJANJA, ura, zacetekZaApi, type Termin } from './termini';

type Oseba = Shema['OsebaPodatki'];
type Mehanik = Shema['MehanikNaloga'];

/** Ure za izbiro začetka (na 15 minut od 6.00 do 20.45). */
const URE = Array.from({ length: 15 * 4 }, (_, i) => {
  const h = 6 + Math.floor(i / 4);
  const m = String((i % 4) * 15).padStart(2, '0');
  return { value: `${String(h).padStart(2, '0')}:${m}`, label: `${h}.${m}` };
});

/** Kje se odpre okno za nov termin (klik na prosto mesto v koledarju) – dan »YYYY-MM-DD« in ura »08:30«. */
export type NovTermin = { dan: string; cas: string };

/**
 * Okno termina v koledarju delavnice: nov termin (stranka pokliče) ali sprememba. Stranka je obvezna (izbrana, nova z
 * imenom in telefonom ali lastnik vozila), vozilo ne – stranka ob klicu pogosto ne pozna registrske. Ob shranjevanju
 * gre stranki potrditev po e-pošti, če je kljukica vključena (privzeto pri novem terminu). Pri obstoječem terminu
 * »Sprejem vozila« odpre sprejem s podatki termina, »Odpovej termin« ga izbriše; sprejet termin samo pokaže nalog.
 */
export function TerminOkno({
  termin,
  nov,
  mehaniki,
  zapri,
}: {
  termin: Termin | null;
  nov: NovTermin | null;
  mehaniki: Mehanik[];
  zapri: () => void;
}) {
  const odprto = !!termin || !!nov;
  const naslov = termin
    ? termin.nalogId
      ? 'Termin (vozilo je sprejeto)'
      : 'Termin'
    : nov
      ? `Nov termin – ${imeDne(nov.dan)}`
      : '';
  return (
    <Modal opened={odprto} onClose={zapri} title={naslov} size="lg">
      {odprto && (
        <Vsebina
          key={termin?.id ?? `${nov?.dan}-${nov?.cas}`}
          termin={termin}
          nov={nov}
          mehaniki={mehaniki}
          zapri={zapri}
        />
      )}
    </Modal>
  );
}

function Vsebina({
  termin,
  nov,
  mehaniki,
  zapri,
}: {
  termin: Termin | null;
  nov: NovTermin | null;
  mehaniki: Mehanik[];
  zapri: () => void;
}) {
  const odjemalec = useQueryClient();
  const [dan, setDan] = useState<string | null>(termin?.zacetek?.slice(0, 10) ?? nov?.dan ?? null);
  const [cas, setCas] = useState<string | null>(termin?.zacetek?.slice(11, 16) ?? nov?.cas ?? '08:00');
  const [trajanje, setTrajanje] = useState<string | null>(String(termin?.trajanje ?? 60));
  const [vozilo, setVozilo] = useState<VoziloStranke | null>(termin?.vozilo ?? null);
  const [novo, setNovo] = useState<VoziloStranke | null>(null);
  const [kupec, setKupec] = useState<Oseba | null>(null);
  const [nova, setNova] = useState<NovaStranka>(null);
  const [opis, setOpis] = useState(termin?.opis ?? '');
  const [mehanik, setMehanik] = useState<string | null>(termin?.mehanikId ? String(termin.mehanikId) : null);
  const [potrditev, setPotrditev] = useState(!termin);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);
  const [odpoved, setOdpoved] = useState(false);
  const sprejet = !!termin?.nalogId;

  const naloziStranko = async (id: number) => {
    try {
      setKupec(await podatki(api.GET('/api/osebe/{vrsta}/{id}', { params: { path: { vrsta: 'kupci', id } } })));
    } catch {
      // stranko lahko izbere sam
    }
  };

  useEffect(() => {
    if (termin?.kupecId) {
      void podatki(api.GET('/api/osebe/{vrsta}/{id}', { params: { path: { vrsta: 'kupci', id: termin.kupecId } } }))
        .then(setKupec)
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- samo ob odprtju okna
  }, []);

  const trajanja = TRAJANJA.some((t) => t.value === trajanje)
    ? TRAJANJA
    : [...TRAJANJA, { value: trajanje ?? '', label: `${trajanje} min` }];

  const osvezi = async () => {
    await odjemalec.invalidateQueries({ queryKey: ['termini'] });
    await odjemalec.invalidateQueries({ queryKey: ['delavnica-pregled'] });
  };

  const shrani = async () => {
    setNapaka(null);
    setPolja({});
    setShranjujem(true);
    try {
      let stranka = kupec;
      if (!stranka && nova && vpisanaNova(nova)) {
        try {
          stranka = await ustvariStranko(nova);
        } catch (e) {
          if (e instanceof NapakaApi && e.polja.ime) {
            setPolja({ 'stranka.ime': e.polja.ime });
            return;
          }
          throw e;
        }
        setKupec(stranka);
        setNova(null);
      }
      const body = {
        zacetek: dan && cas ? zacetekZaApi(dan, cas) : undefined,
        trajanje: trajanje ? Number(trajanje) : undefined,
        kupecId: stranka?.id ?? undefined,
        voziloId: novo ? undefined : (vozilo?.id ?? undefined),
        vozilo: novo ?? undefined,
        opis: obrezano(opis),
        mehanikId: mehanik ? Number(mehanik) : undefined,
      };
      const t = termin?.id
        ? await podatki(api.PUT('/api/delavnica/termini/{id}', { params: { path: { id: termin.id } }, body }))
        : await podatki(api.POST('/api/delavnica/termini', { body }));
      await osvezi();
      notifications.show({
        color: 'green',
        message: `Termin je shranjen: ${imeDne(t.zacetek ?? '')} ob ${ura(t.zacetek)}.`,
      });
      if (potrditev) {
        if (!t.email) {
          notifications.show({ color: 'yellow', message: 'Stranka nima e-pošte, potrditev ni poslana.' });
        } else {
          try {
            await podatki(api.POST('/api/delavnica/termini/{id}/potrditev', { params: { path: { id: t.id! } } }));
            await osvezi();
            notifications.show({ color: 'green', message: `Potrditev je poslana na ${t.email}.` });
          } catch (e) {
            notifications.show({ color: 'red', message: `Potrditev ni poslana: ${sporocilo(e)}` });
          }
        }
      }
      zapri();
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setShranjujem(false);
    }
  };

  const odpovej = async () => {
    setNapaka(null);
    setShranjujem(true);
    try {
      await podatki(api.DELETE('/api/delavnica/termini/{id}', { params: { path: { id: termin!.id! } } }));
      await osvezi();
      notifications.show({ color: 'green', message: 'Termin je odpovedan.' });
      zapri();
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setShranjujem(false);
    }
  };

  if (sprejet) {
    return (
      <Stack gap="sm">
        <Text>
          {imeDne(termin.zacetek ?? '')} ob {ura(termin.zacetek)}–{ura(termin.konec)}
        </Text>
        <Text fw={600}>
          {[termin.vozilo?.registrska, [termin.vozilo?.znamka, termin.vozilo?.model].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(' · ')}
        </Text>
        <Text>{termin.kupec}</Text>
        {termin.opis && <Text c="dimmed">{termin.opis}</Text>}
        <Text>
          Vozilo je sprejeto na nalog{' '}
          <Anchor component={Link} to={`/delavnica/nalogi/${termin.nalogId}`} fw={600}>
            {termin.stevilkaNaloga}
          </Anchor>
          .
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {napaka && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="alert">
          {napaka}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
        <DatePickerInput
          label="Datum"
          value={dan}
          onChange={(d) => setDan(datumZaApi(d) ?? null)}
          valueFormat="D. M. YYYY"
          error={polja.zacetek}
          withAsterisk
        />
        <Select label="Ura" data={URE} value={cas} onChange={setCas} allowDeselect={false} searchable withAsterisk />
        <Select
          label="Trajanje"
          data={trajanja}
          value={trajanje}
          onChange={setTrajanje}
          error={polja.trajanje}
          allowDeselect={false}
        />
      </SimpleGrid>
      <StrankaNaloga kupec={kupec} izberi={setKupec} nova={nova} nastaviNovo={setNova} polja={polja} />
      {novo ? (
        <Stack gap="sm">
          <PoljaVozila vozilo={novo} nastavi={setNovo} polja={polja} predpona="vozilo." />
          <Group>
            <Button variant="subtle" onClick={() => setNovo(null)}>
              Poišči vpisano vozilo
            </Button>
          </Group>
        </Stack>
      ) : (
        <Group align="flex-end" gap="sm" wrap="nowrap">
          <div style={{ flex: 1, minWidth: 0 }}>
            <IzbiraVozila
              izbrano={vozilo}
              izberi={(v) => {
                setVozilo(v);
                if (v?.kupecId && !kupec && !nova) {
                  void naloziStranko(v.kupecId);
                }
              }}
              napaka={polja.voziloId}
              obvezno={false}
            />
          </div>
          <Button
            variant="light"
            leftSection={<IconPlus size={18} />}
            onClick={() => {
              setVozilo(null);
              setNovo({});
            }}
          >
            Novo vozilo
          </Button>
        </Group>
      )}
      <Textarea
        label="Kaj je treba narediti"
        value={opis}
        onChange={(e) => setOpis(e.currentTarget.value)}
        onBlur={() => setOpis((t) => t.trim())}
        error={polja.opis}
        autosize
        minRows={2}
      />
      <Select
        label="Mehanik"
        data={mehaniki.map((m) => ({ value: String(m.id), label: m.ime ?? '' }))}
        value={mehanik}
        onChange={setMehanik}
        error={polja.mehanikId}
        clearable
      />
      <Checkbox
        label={termin?.potrjeno ? 'Pošlji stranki novo potrditev po e-pošti' : 'Pošlji stranki potrditev po e-pošti'}
        checked={potrditev}
        onChange={(e) => setPotrditev(e.currentTarget.checked)}
      />
      <Group justify="space-between" mt="sm" gap="sm">
        <Group gap="xs">
          {termin?.id &&
            (odpoved ? (
              <>
                <Button color="red" onClick={() => void odpovej()} loading={shranjujem}>
                  Potrdi odpoved
                </Button>
                <Button variant="subtle" onClick={() => setOdpoved(false)}>
                  Prekliči
                </Button>
              </>
            ) : (
              <Button variant="subtle" color="red" onClick={() => setOdpoved(true)}>
                Odpovej termin
              </Button>
            ))}
        </Group>
        <Group gap="xs">
          {termin?.id && (
            <Button
              component={Link}
              to={`/delavnica/sprejem?termin=${termin.id}`}
              variant="light"
              leftSection={<IconTruckDelivery size={18} />}
            >
              Sprejem vozila
            </Button>
          )}
          <Button onClick={() => void shrani()} loading={shranjujem}>
            Shrani
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}
